import React, {
  Dispatch,
  MutableRefObject,
  RefObject,
  SetStateAction,
  useMemo,
  useRef,
} from 'react'
import './TextEditor.css'
import useTextEditor, { IEditorInput } from './useTextEditor'
import {
  EmojiPicker,
  HtmlEditor,
  Inject,
  Link,
  QuickToolbar,
  RichTextEditorComponent,
  Toolbar,
  ToolbarSettingsModel,
} from '@syncfusion/ej2-react-richtexteditor'
import { CHAT_LEFT_BUTTONS, CHAT_RIGHT_BUTTONS } from '@constants/Message'
import { MentionComponent } from '@syncfusion/ej2-react-dropdowns'
import { VariableList } from '@components/pages/Messages/MockDataMessages'
import { generateSvg } from '@utils/svg.util'

export interface ITextEditorProps {
  editorInput: IEditorInput
  enableEnterNextLine?: boolean
  enableMention?: boolean
  enableVariable?: boolean
  isAiEnabled: boolean
  setEditorInput: Dispatch<SetStateAction<IEditorInput>>
  setIsAiEnabled: Dispatch<SetStateAction<boolean>>
  handleSend: () => void
  hideToolbar?: boolean
  emailFormattingToolbar?: boolean
  isEditing?: boolean
  isEmojiEnabled?: boolean
  isLinkEnabled?: boolean
  isMentionEnabled?: boolean
  isVariableEnabled?: boolean
  onSaveEdit?: () => void
  resize?: boolean
  setIsEditing?: Dispatch<SetStateAction<boolean>>
  selectionRef?: MutableRefObject<Range | null>
  toolbarRef?: RefObject<RichTextEditorComponent>
  _variableList?: { text: string; value: string }[]
  _variableRef?: RefObject<MentionComponent>
  persistAiMode?: boolean // Text box is always in AI mode
  hideAiQuickActions?: boolean // Hide AI quick actions (Reply / Summarize)
  showAiToolbarToggle?: boolean // Hide AI toggle button
  rteId?: string // To prevent errors in unmounting for multiple editors
  className?: string
  showClearButton?: boolean
}

const TextEditor: React.FC<ITextEditorProps> = ({
  editorInput,
  enableEnterNextLine = false,
  enableMention = true,
  enableVariable = true,
  isAiEnabled,
  setEditorInput,
  setIsAiEnabled,
  handleSend,
  hideToolbar = false,
  emailFormattingToolbar = false,
  isEditing,
  isEmojiEnabled = true,
  isLinkEnabled = true,
  isMentionEnabled = true,
  isVariableEnabled = true,
  onSaveEdit,
  resize = false,
  selectionRef,
  setIsEditing,
  toolbarRef,
  _variableList,
  _variableRef,
  persistAiMode = false,
  hideAiQuickActions = false,
  showAiToolbarToggle = true,
  rteId = 'target-input',
  className,
  showClearButton = true,
}) => {
  const internalToolbarRef = useRef<RichTextEditorComponent>(null)
  const effectiveToolbarRef = toolbarRef ?? internalToolbarRef
  const { ...hooks } = useTextEditor(
    editorInput,
    enableEnterNextLine,
    hideToolbar && !emailFormattingToolbar,
    isAiEnabled,
    setEditorInput,
    setIsAiEnabled,
    handleSend,
    effectiveToolbarRef,
    isEditing,
    onSaveEdit,
    setIsEditing,
    selectionRef,
    persistAiMode,
  )

  const filteredButtons = useMemo(() => {
    return CHAT_LEFT_BUTTONS.filter((button) => {
      if (button.key === 'ai' && !showAiToolbarToggle) {
        return false
      }
      switch (button.key) {
        case 'ai':
          return true
        case 'emoji':
          return isEmojiEnabled
        case 'url':
          return isLinkEnabled
        case 'mention':
          return isMentionEnabled
        case 'variable':
          return isVariableEnabled
      }
      return false
    })
  }, [
    isEmojiEnabled,
    isLinkEnabled,
    isMentionEnabled,
    isVariableEnabled,
    showAiToolbarToggle,
  ])

  const emailToolbarSettingsModel = useMemo(
    (): ToolbarSettingsModel => ({
      enable: false,
      items: [],
    }),
    [],
  )

  const chatToolbarSettingsModel = useMemo((): ToolbarSettingsModel => {
    return {
      items: filteredButtons.map((data) => {
        if (data.type === 'builtin') {
          return data.icon
        }
        return {
          template: generateSvg(
            data.key,
            data.icon,
            data.type === 'ti' ? 24 : 16,
          ),
          undo: true,
          tooltipText: data.text,
          click: () => {
            hooks.handleToolbarActions(data.key)
          },
        }
      }),
      enable: !hideToolbar,
    }
  }, [filteredButtons, hideToolbar, hooks.handleToolbarActions])

  const toolbarSettings: ToolbarSettingsModel = emailFormattingToolbar
    ? emailToolbarSettingsModel
    : chatToolbarSettingsModel

  const richTextEditorMountKey = useMemo(
    () =>
      emailFormattingToolbar
        ? 'rte-em'
        : rteId === 'target-input'
          ? 'rte-ch'
          : `rte-ch-${rteId}`,
    [emailFormattingToolbar, rteId],
  )

  const injectedServices = useMemo(
    () => [Toolbar, Link, HtmlEditor, QuickToolbar, EmojiPicker],
    [],
  )

  return (
    <div
      className={`text-editor-comp${emailFormattingToolbar ? ' text-editor-comp--email-format' : ''} ${hooks.isRecording ? 'disabled' : ''}${className ? ` ${className}` : ''}`.trim()}
    >
      <div className={isAiEnabled ? 'border' : undefined}>
        {hooks.isRecording && (
          <div className="mcc-audio-rec">
            <span className="mgc_mic_fill" /> 00:00
          </div>
        )}
        <RichTextEditorComponent
          key={richTextEditorMountKey}
          id={rteId}
          ref={hooks.toolbarRef}
          toolbarSettings={toolbarSettings}
          actionBegin={hooks.onActionBegin}
          value={editorInput.htmlContent}
          enableTabKey={true}
          enterKey="BR"
          cssClass="message-rte-tooltip"
          className={
            emailFormattingToolbar
              ? 'rte-email-format-shell'
              : isAiEnabled
                ? 'glow'
                : 'default'
          }
          placeholder={
            isAiEnabled ? 'What would you like AI to do?' : 'Type a message'
          }
          style={resize ? { resize: 'vertical' } : undefined}
          change={hooks.onTextChange}
          created={hooks.onCreated}
          onClick={hooks.saveCaretPosition}
          focus={hooks.saveCaretPosition}
        >
          <Inject services={injectedServices} />
        </RichTextEditorComponent>
        {!hideToolbar && !emailFormattingToolbar && (
          <div className="d-flex chat-right-buttons">
            {CHAT_RIGHT_BUTTONS.map((data, index) => (
              <button
                type="button"
                data-bs-toggle="tooltip"
                title={data.text}
                aria-label={data.text}
                key={index}
                id={`custom-${data.key}-button`}
                className={`unstyled-button ${hooks.isRecording ? 'active' : ''}`}
                onClick={() => {
                  hooks.handleRightButtonsAction(data.key)
                }}
              >
                <i className={`${data.icon} ${data.key}-icon`}></i>
              </button>
            ))}
          </div>
        )}
        {(isAiEnabled || isEditing) && showClearButton && (
          <div className="d-flex justify-content-between x-button">
            <button
              type="button"
              data-bs-toggle="tooltip"
              title="Clear"
              className="unstyled-button border-0 me-1"
              onClick={hooks.handleClearBtn}
            >
              <i className="mgc_close_line"></i>
            </button>
          </div>
        )}
        {isAiEnabled && !hideAiQuickActions && (
          <div className="ai-toolbar-buttons-container pe-2">
            <button
              type="button"
              data-bs-toggle="tooltip"
              title="reply"
              className="unstyled-button border-0 ai-toolbar-buttons"
              onClick={() => hooks.handleAIFeaturesButtons('reply')}
            >
              Reply to this message
            </button>
            <button
              type="button"
              data-bs-toggle="tooltip"
              title="summarize"
              className="unstyled-button border-0 ai-toolbar-buttons"
              onClick={() => hooks.handleAIFeaturesButtons('summarize')}
            >
              Summarize
            </button>
          </div>
        )}
      </div>
      <div className="tec-mention-comp">
        {enableVariable && (
          <MentionComponent
            target={`#${rteId}`}
            ref={_variableRef ?? hooks.variableRef}
            dataSource={_variableList ?? VariableList}
            showMentionChar={false}
            mentionChar="("
            cssClass="variable-component-text"
            fields={{ text: 'text', value: 'value' }}
            suffixText={'&nbsp;'}
          />
        )}
        {enableMention && (
          <MentionComponent
            target={`#${rteId}`}
            ref={hooks.mentionRef}
            dataSource={hooks.peopleList}
            showMentionChar={true}
            mentionChar="@"
            cssClass="mention-component-text"
            fields={{ text: 'name', value: 'name' }}
            suffixText={'&nbsp;'}
          />
        )}
      </div>
    </div>
  )
}

export default TextEditor
